import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';

type GroupOnboardingForm = {
  fullName: string;
  heightCm: string;
  weightKg: string;
  goalWeightKg: string;
  gender: 'male' | 'female' | '';
  workoutLocation: 'home' | 'gym' | 'mix' | '';
  workoutPreferences: string;
  goal: string;
  trainingExperience: 'beginner' | 'intermediate' | 'advanced';
  injuriesOrLimitations: string;
  availabilityDaysPerWeek: 3 | 4;
  equipmentAccess: string;
  anythingElse: string;
};

const parseOptionalNumber = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return undefined;
  return parsed;
};

const GroupOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<GroupOnboardingForm>({
    fullName: '',
    heightCm: '',
    weightKg: '',
    goalWeightKg: '',
    gender: '',
    workoutLocation: '',
    workoutPreferences: '',
    goal: '',
    trainingExperience: 'beginner',
    injuriesOrLimitations: '',
    availabilityDaysPerWeek: 3,
    equipmentAccess: '',
    anythingElse: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = <K extends keyof GroupOnboardingForm>(key: K, value: GroupOnboardingForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError(null);
    setIsSubmitting(true);

    try {
      const payload = {
        // New questions (stored with consistent snake_case keys)
        name: form.fullName.trim(),
        height_cm: parseOptionalNumber(form.heightCm),
        weight_kg: parseOptionalNumber(form.weightKg),
        goal_weight_kg: parseOptionalNumber(form.goalWeightKg),
        gender: form.gender || undefined,
        workout_location: form.workoutLocation || undefined,
        workout_preferences: form.workoutPreferences.trim() || undefined,

        // Existing questions (mapped to snake_case keys)
        goal: form.goal.trim(),
        training_experience: form.trainingExperience,
        injuries_or_limitations: form.injuriesOrLimitations.trim() || undefined,
        availability_days_per_week: form.availabilityDaysPerWeek,
        equipment_access: form.equipmentAccess.trim() || undefined,
        anything_else: form.anythingElse.trim() || undefined,

        submitted_from: 'web',
      };

      const response = await apiService.submitGroupOnboarding({
        ...payload,
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to submit onboarding');
      }

      navigate('/dashboard');
    } catch (err: any) {
      setError(err?.message || 'Failed to submit onboarding. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8">
        <h1 className="text-2xl font-bold text-gray-900">Group Coaching Onboarding</h1>
        <p className="mt-2 text-gray-600">
          Answer a few questions so we can tailor your plan and get you started quickly.
        </p>

        {error && (
          <div className="mt-6 p-4 rounded-md bg-red-50 text-red-800 border border-red-200">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="sm:col-span-2">
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                Name *
              </label>
              <input
                id="fullName"
                type="text"
                required
                value={form.fullName}
                onChange={(e) => handleChange('fullName', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                placeholder="Your full name"
                autoComplete="name"
              />
            </div>

            <div>
              <label htmlFor="gender" className="block text-sm font-medium text-gray-700">
                Gender *
              </label>
              <select
                id="gender"
                required
                value={form.gender}
                onChange={(e) => handleChange('gender', e.target.value as GroupOnboardingForm['gender'])}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              >
                <option value="" disabled>
                  Select…
                </option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            <div>
              <label htmlFor="workoutLocation" className="block text-sm font-medium text-gray-700">
                Workout location *
              </label>
              <select
                id="workoutLocation"
                required
                value={form.workoutLocation}
                onChange={(e) => handleChange('workoutLocation', e.target.value as GroupOnboardingForm['workoutLocation'])}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              >
                <option value="" disabled>
                  Select…
                </option>
                <option value="home">Home</option>
                <option value="gym">Gym</option>
                <option value="mix">Mix</option>
              </select>
            </div>

            <div>
              <label htmlFor="heightCm" className="block text-sm font-medium text-gray-700">
                Height (cm) *
              </label>
              <input
                id="heightCm"
                type="number"
                inputMode="numeric"
                required
                min={80}
                max={260}
                value={form.heightCm}
                onChange={(e) => handleChange('heightCm', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                placeholder="e.g. 175"
              />
            </div>

            <div>
              <label htmlFor="weightKg" className="block text-sm font-medium text-gray-700">
                Weight (kg) *
              </label>
              <input
                id="weightKg"
                type="number"
                inputMode="decimal"
                required
                min={20}
                max={400}
                step="0.1"
                value={form.weightKg}
                onChange={(e) => handleChange('weightKg', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                placeholder="e.g. 82.5"
              />
            </div>

            <div>
              <label htmlFor="goalWeightKg" className="block text-sm font-medium text-gray-700">
                Goal weight (kg) *
              </label>
              <input
                id="goalWeightKg"
                type="number"
                inputMode="decimal"
                required
                min={20}
                max={400}
                step="0.1"
                value={form.goalWeightKg}
                onChange={(e) => handleChange('goalWeightKg', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                placeholder="e.g. 75"
              />
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="workoutPreferences" className="block text-sm font-medium text-gray-700">
                Workout likes / dislikes
              </label>
              <textarea
                id="workoutPreferences"
                rows={3}
                value={form.workoutPreferences}
                onChange={(e) => handleChange('workoutPreferences', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                placeholder="e.g. love upper body, dislike burpees, prefer short sessions…"
              />
            </div>
          </div>

          <div>
            <label htmlFor="goal" className="block text-sm font-medium text-gray-700">
              Primary goal *
            </label>
            <textarea
              id="goal"
              required
              rows={3}
              value={form.goal}
              onChange={(e) => handleChange('goal', e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              placeholder="e.g. lose fat, build muscle, improve fitness…"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="trainingExperience" className="block text-sm font-medium text-gray-700">
                Training experience *
              </label>
              <select
                id="trainingExperience"
                value={form.trainingExperience}
                onChange={(e) => handleChange('trainingExperience', e.target.value as GroupOnboardingForm['trainingExperience'])}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>

            <div>
              <label htmlFor="availabilityDaysPerWeek" className="block text-sm font-medium text-gray-700">
                Days per week available *
              </label>
              <select
                id="availabilityDaysPerWeek"
                value={form.availabilityDaysPerWeek}
                onChange={(e) => handleChange('availabilityDaysPerWeek', Number(e.target.value) as 3 | 4)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              >
                <option value={3}>3 days/week</option>
                <option value={4}>4 days/week</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="injuriesOrLimitations" className="block text-sm font-medium text-gray-700">
              Injuries or limitations
            </label>
            <textarea
              id="injuriesOrLimitations"
              rows={3}
              value={form.injuriesOrLimitations}
              onChange={(e) => handleChange('injuriesOrLimitations', e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              placeholder="Anything we should know to keep training safe?"
            />
          </div>

          <div>
            <label htmlFor="equipmentAccess" className="block text-sm font-medium text-gray-700">
              Equipment access
            </label>
            <input
              id="equipmentAccess"
              type="text"
              value={form.equipmentAccess}
              onChange={(e) => handleChange('equipmentAccess', e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              placeholder="e.g. full gym, home dumbbells, bands only…"
            />
          </div>

          <div>
            <label htmlFor="anythingElse" className="block text-sm font-medium text-gray-700">
              Anything else?
            </label>
            <textarea
              id="anythingElse"
              rows={3}
              value={form.anythingElse}
              onChange={(e) => handleChange('anythingElse', e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              placeholder="Anything you want your coach to know before we start?"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting…' : 'Continue to Dashboard'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GroupOnboarding;

